import { useContext } from "react";
import { SegmentGroup, Segment, Text, Flex } from "@simplybusiness/mobius";
import { CombinedPaymentContext } from "../CombinedPaymentContext";
import { PaymentItemsSection } from "./PaymentItemsSection";

export const PaymentSummary = ({
  primaryTrade,
  businessName,
  upfrontPayments,
  upfrontPaymentsTotal,
  monthlyPayments,
  monthlyPaymentSummary,
  firstMonthlyPaymentDate,
  totalPaymentAmount,
  nonRefundableTexts,
  surchargeTexts,
  multiProductText,
  cardFeeDisclosureText,
  isExternalCarrier,
  hasMonthlyFees,
}: PaymentSummaryProps) => {
  const isCombinedPaymentPage = useContext(CombinedPaymentContext);

  const areMonthlyPaymentsAvailable = monthlyPayments.length > 0;
  const upfrontPaymentsSummaryItem = () => {
    if (areMonthlyPaymentsAvailable) {
      return [
        {
          label: "To pay today",
          value: upfrontPaymentsTotal,
        },
      ];
    }

    return undefined;
  };

  const monthlyPaymentsHeaderItem = {
    label: "First monthly payment taken",
    value: firstMonthlyPaymentDate,
  };

  return (
    <div>
      <SegmentGroup
        data-testid="payment-summary"
        className={isCombinedPaymentPage ? "combined-styles" : undefined}
      >
        {!isCombinedPaymentPage ? (
          <Segment data-testid="business-information-section">
            <div className="mobius-payment-summary__item-grid">
              <Text variant="h4">{`${primaryTrade} insurance`}</Text>
              <Text variant="small" spacing="tight">
                <strong>{businessName}</strong>
              </Text>
            </div>
          </Segment>
        ) : null}
        <PaymentItemsSection
          data-testid="today-payment-section"
          headerText="Today's payment"
          items={upfrontPayments}
          summaryItems={upfrontPaymentsSummaryItem()}
        />
        {areMonthlyPaymentsAvailable && (
          <PaymentItemsSection
            data-testid="monthly-payment-section"
            headerText="Monthly payments"
            headerItem={monthlyPaymentsHeaderItem}
            items={monthlyPayments}
            summaryItems={hasMonthlyFees ? monthlyPaymentSummary : undefined}
            isExternalCarrier={isExternalCarrier}
          />
        )}
        <Segment colour="subtle">
          {!isCombinedPaymentPage ? (
            <Flex
              className="mobius-payment-summary__styled-flex"
              data-testid="total-payment-amount"
            >
              <Text>Total (inc. tax + fees)</Text>
              <Text>{totalPaymentAmount}</Text>
            </Flex>
          ) : (
            <Flex
              className="mobius-payment-summary__styled-flex"
              data-testid="total-payment-amount"
            >
              <Text>
                <strong>Total</strong> (inc. tax + fees)
              </Text>
              <Text>
                <strong>{totalPaymentAmount}</strong>
              </Text>
            </Flex>
          )}
        </Segment>
      </SegmentGroup>
      <Text variant="legal">{multiProductText}</Text>
      {nonRefundableTexts.map((text, index) => (
        <Text variant="legal" key={["non-refundable-text", index].join("-")}>
          {text}
        </Text>
      ))}

      {surchargeTexts.map((text, index) => (
        <Text variant="legal" key={["surcharge-text", index].join("-")}>
          {text}
        </Text>
      ))}

      <Text variant="legal">{cardFeeDisclosureText}</Text>
    </div>
  );
};

type PaymentItem = {
  labels: string[];
  value: string;
};

type SummaryItems = { label: string; value: string }[];

export type PaymentSummaryProps = {
  primaryTrade: string;
  businessName: string;
  upfrontPayments: PaymentItem[];
  upfrontPaymentsTotal: string;
  monthlyPayments: PaymentItem[];
  monthlyPaymentSummary: SummaryItems;
  firstMonthlyPaymentDate: string;
  totalPaymentAmount: string;
  nonRefundableTexts: string[];
  surchargeTexts: string[];
  multiProductText: string;
  cardFeeDisclosureText: string;
  isExternalCarrier: boolean;
  hasMonthlyFees: boolean;
};
